import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import { saveAs } from 'file-saver'
import { RoleIsReadOnly } from '../../../components/role'
import ErrorDisplay from '../../../components/error/ErrorDisplay'
// import CopyFileModal from '../../modal/CopyFile'
import { SessionView } from '../../session/Session'
import Editor, { OnErrorFunc, OnFinishedFunc, Problem } from '../Editor'

import { Box, Grid, IconButton, Typography, useTheme } from '@mui/material'
import styles from './Bloblang.module.css'
import { getHeaders } from '../../../utils/common'
import StatusBar from '../../../components/StatusBar'
import Loading from '../../../Loading'

/**
 * @type {LoadResponse}
 */
type LoadResponse = {
  value: string
  errors: Problem[]
  run_input: string
  exec_result: string
  exec_error: string
  view?: SessionView
}

type appParams = {
  sessionID: string
  fileName: string
}

/**
 * bloblang editor -- require input to map
 * @returns {JSX.Element}
 */
export default function App() {
  const navigate = useNavigate()
  const theme =useTheme()
  const { sessionID, fileName } = useParams<appParams>()

  // Copy file modal
  const [copyFileModalIsOpen, setCopyFileModalIsOpen] = useState(false)

  const [isLoading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)

  const [mapping, setMapping] = useState('')
  const [runInput, setRunInput] = useState('')
  const [testOutput, setTestOutput] = useState('')
  const [errors, setErrors] = useState<Problem[]>([])
  const [readOnly, setReadOnly] = useState(true)
  const [saveState, setSaveState] = useState('')
  const [sessView, setSessView] = useState<SessionView | null>(null)

  const bloblURL = `/mqttapi/v1/session/${sessionID}/file/${fileName}/blobl`
  const bloblConfigURL = `/mqttapi/v1/session/${sessionID}/file_config/${fileName}/blobl`

  const myHeaders = getHeaders()
  useEffect(() => {
    let subscribed = true

    setSaveState('')
    setLoading(true)

    fetch(bloblURL, { headers: myHeaders, method: 'GET' })
      .then(async (response) => {
        if (response.ok) {
          setFetchError(null)
          return response.json()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then((json: LoadResponse) => {
        if (!subscribed) {
          return
        }

        setMapping(json.value)
        setRunInput(json.run_input || '')
        setErrors(json.errors)
        setReadOnly(RoleIsReadOnly(json.view?.user_role || ''))
        setTestOutput(json.exec_result || json.exec_error)
        setFetchError(null)
        setSessView(json.view || null)
      })
      .catch((error) => (subscribed ? setFetchError(error) : null))
      .finally(() => (subscribed ? setLoading(false) : null))

    return () => {
      subscribed = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloblURL, sessionID, fileName])

  const saveMapping = useCallback(
    (saveMapping: string | null, onFinished: OnFinishedFunc, onError: OnErrorFunc) => {
      let subscribed = true

      setSaveState('saving')
      fetch(bloblURL, {
        method: 'POST',
        headers: myHeaders,
        body: saveMapping,
      })
        .then(async (response) => {
          if (response.ok) {
            return response.json()
          }
          const text = await response.text()
          throw new Error(`${response.statusText}: ${text}`)
        })
        .then((json) => {
          if (!subscribed) {
            return
          }
          setSaveState('saved')
          setTestOutput(json.exec_result || json.exec_error)
          onFinished(json.lints, json.errors)
        })
        .catch((error) => {
          if (!subscribed) {
            return
          }
          setSaveState('failed')
          onError(error)
        })

      return () => (subscribed = false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionID, fileName, bloblURL]
  )

  const saveTestInput = useCallback(
    (saveInput:any, onSave:any, onError:any) => {
      let subscribed = true

      fetch(bloblConfigURL, {
        method: 'POST',
        headers: myHeaders,
        body: saveInput,
      })
        .then(async (response) => {
          if (response.ok) {
            return response.json()
          }
          const text = await response.text()
          throw new Error(`${response.statusText}: ${text}`)
        })
        .then((json) => {
          if (!subscribed) {
            return
          }
          onSave([], [])
          setTestOutput(json.exec_result || json.exec_error)
        })
        .catch((error) => {
          if (!subscribed) {
            return
          }
          onError()
        })

      return () => (subscribed = false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionID, fileName, bloblConfigURL]
  )

  const downloadFile = () => {
    const url = `/mqttapi/v1/session/${sessionID}/download/${fileName}`
    fetch(url, { headers: myHeaders, method: 'GET' })
      .then(async (response) => {
        if (response.ok) {
          return response.json()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then((res) => {
        const blob = new Blob([res], {
          type: 'application/json;charset=utf-8',
        })
        saveAs(blob, `${fileName}`)
      })
      .catch((error) => console.log(error))
  }

  return (
    <Box style={{ height: '88vh', width: '100%',  padding: theme.spacing(2)  }}>
      {/* <CopyFileModal
        sessionID={sessionID}
        target={decodeURIComponent(fileName)}
        onCopy={(sessionID: string, fileName: string) => {
          history.push(`/app/view/s/${sessionID}/${encodeURIComponent(fileName)}/blobl`)
        }}
        isOpen={copyFileModalIsOpen}
        closeModal={() => setCopyFileModalIsOpen(false)}
        organisation={
          sessView?.organisation_name
            ? {
                id: sessView.owner_id,
                name: sessView.organisation_name,
              }
            : undefined
        }
      /> */}
      <StatusBar
        crumbs={{
          file: fileName,
          session: {
            id: sessionID || '',
            name: sessView?.session_name || '',
          },
          organisation: sessView?.organisation_name
            ? {
                id: sessView.owner_id,
                name: sessView.organisation_name,
              }
            : undefined,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            title="copy file to..."
            onClick={() => {
              setCopyFileModalIsOpen(true)
            }}
          >
            <ContentCopyIcon />
          </IconButton>
          <IconButton title="download file" onClick={downloadFile}>
            <DownloadIcon />
          </IconButton>
        </Box>
        <small className={styles.savingText}>{saveState}</small>
      </StatusBar>
      <Box>
        {isLoading ? (
          <Loading />
        ) : fetchError !== null ? (
          <ErrorDisplay err={fetchError} url={bloblURL} />
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item md={6}>
                <Typography sx={{ p: 1 }} align="center" variant="h6" borderRadius={1}>
                  Test input
                </Typography>
                <Editor
                  name="bstdio_blobl_input_editor"
                  contents={runInput || ''}
                  mode="text"
                  readOnly={readOnly}
                  onSave={saveTestInput}
                  onChange={(value: string) => {
                    setRunInput(value)
                  }}
                />
              </Grid>
              <Grid item md={6}>
                <Typography sx={{ p: 1 }} align="center" variant="h6" borderRadius={1}>
                  Test output
                </Typography>
                <Editor
                  name="bstdio_blobl_output_editor"
                  contents={testOutput}
                  mode="text"
                  readOnly={true}
                  onSave={() => {}}
                  onChange={() => {}}
                  hideControls={true}
                />
              </Grid>
              <Grid item md={12}>
                <Typography sx={{ p: 1 }} align="center" variant="h6" borderRadius={1}>
                  Mapping
                </Typography>
                <Editor
                  name="bstdio_blobl_editor"
                  contents={mapping}
                  initErrors={errors}
                  mode="coffee"
                  readOnly={readOnly}
                  onSave={saveMapping}
                  onChange={(value: string) => {
                    setSaveState('pending')
                    setMapping(value)
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  )
}
